import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import Loader from "atoms/loaders/loader";
import Paragraph from "atoms/typography/paragraph";
import {
    BlockDestinationRecord,
    ExistingPageDestinationRecord,
    NewNotebookDestinationRecord,
    NewPageDestinationRecord,
} from "models/view-models/populated-blocks/block-destination-record";
import NotebookBlockSelectorList from "organisms/notebook-block-selector/notebook-block-selector-list";
import React, { useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { t } from "utilities/localization-utils";
import NotebookService from "utilities/services/notebooks/notebook-service";
import StringUtils from "utilities/string-utils";
import { ToastManager } from "utilities/toast/toast-manager";

interface NotebookBlockSelectorProps {
    onSubmit: (destination: BlockDestinationRecord) => Promise<void>;
}

const CSS_CLASS_NAME = "c-notebook-block-selector";
const SUGGESTION_CLASS = "c-suggestion";

const NotebookBlockSelector = ({ onSubmit }: NotebookBlockSelectorProps) => {
    const [newNotebookName, setNewNotebookName] = useState("");
    const [newPageName, setNewPageName] = useState("");
    const [showPageSelector, setShowPageSelector] = useState(false);
    const [notebookId, setNotebookId] = useState<number>();
    const [pageId, setPageId] = useState<number>();

    const [isLoading, setIsLoading] = useState(false);

    const { list: listNotebooksQuery } = NotebookService.useListQuery();

    const { data: notebookResponse } = listNotebooksQuery({
        findNewlyShared: true,
        editOnly: true,
    });

    const notebooks = notebookResponse?.resultObjects?.map((notebook) => {
        return { name: notebook.name, id: notebook.id };
    });
    const pages = notebookId
        ? notebookResponse?.resultObjects
              ?.find((notebook) => notebook.id === notebookId)
              ?.pages?.map((page) => {
                  return { name: page.name, id: page.id };
              })
        : [];

    const handleBack = () => {
        setShowPageSelector(false);
        setPageId(undefined);
        setNewPageName("");
    };

    const buildDestination = (): BlockDestinationRecord => {
        if (pageId != null) {
            return new ExistingPageDestinationRecord({
                pageId,
            });
        }

        if (notebookId != null) {
            return new NewPageDestinationRecord({
                notebookId,
                pageName: newPageName,
            });
        }

        if (StringUtils.hasValue(newNotebookName)) {
            return new NewNotebookDestinationRecord({
                notebookName: newNotebookName,
            });
        }

        throw new Error("Invalid destination");
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        const destination = buildDestination();
        try {
            await onSubmit(destination);
            ToastManager.success(t("generate-block--success-text"));
        } catch (e) {
            console.error(e);
            ToastManager.error(t("generate-block--error-text"));
        } finally {
            setIsLoading(false);
        }
    };

    const isSubmitButtonDisabled =
        isLoading ||
        (!pageId &&
            StringUtils.isEmpty(newNotebookName) &&
            StringUtils.isEmpty(newPageName));

    const isNextButtonDisabled = !notebookId && !newNotebookName;

    const isSubmitButtonVisible =
        showPageSelector || StringUtils.hasValue(newNotebookName);

    return (
        <section className={`${CSS_CLASS_NAME} ${SUGGESTION_CLASS}`}>
            <header className={`${CSS_CLASS_NAME}__header`}>
                <span className={`${SUGGESTION_CLASS}__tag`}>
                    <Icon
                        type={Icons.Sparkle}
                        size={IconSizes.Small}
                        cssClassName={`${SUGGESTION_CLASS}__tag-icon`}
                    />
                    <span>Add to Notebook</span>
                </span>
            </header>

            {isLoading ? (
                <Loader />
            ) : CollectionUtils.hasValues(notebooks) ? (
                showPageSelector ? (
                    // Page list
                    <NotebookBlockSelectorList
                        selectedRecordId={pageId}
                        helpText={
                            <span>
                                And on what <strong>page</strong> in{" "}
                                <strong>
                                    {StringUtils.hasValue(newNotebookName)
                                        ? newNotebookName
                                        : notebooks.find(
                                              (notebook) =>
                                                  notebook.id === notebookId
                                          )?.name}
                                </strong>
                                ?
                            </span>
                        }
                        records={pages}
                        newRecordName={newPageName}
                        onExistingRecordClick={setPageId}
                        onNewRecordNameChanged={setNewPageName}
                        createButtonText={t("notebooks-create-new-page")}
                    />
                ) : (
                    // Notebook list
                    <NotebookBlockSelectorList
                        selectedRecordId={notebookId}
                        helpText={
                            <span>
                                What <strong>notebook</strong> would you like to
                                add this to?
                            </span>
                        }
                        records={notebooks}
                        newRecordName={newNotebookName}
                        onExistingRecordClick={setNotebookId}
                        onNewRecordNameChanged={setNewNotebookName}
                        createButtonText={t("createNewNotebook")}
                    />
                )
            ) : (
                <Paragraph>{t("none")}</Paragraph>
            )}

            <div className={`${CSS_CLASS_NAME}__actions`}>
                {showPageSelector && (
                    <Button
                        size={ButtonSizes.Small}
                        style={ButtonStyles.Secondary}
                        onClick={handleBack}>
                        {t("back")}
                    </Button>
                )}

                {isSubmitButtonVisible ? (
                    <Button
                        cssClassName={`${CSS_CLASS_NAME}__action-button -next`}
                        size={ButtonSizes.Small}
                        disabled={isSubmitButtonDisabled}
                        onClick={handleSubmit}>
                        {t("generate-block--button-text")}
                    </Button>
                ) : (
                    <Button
                        cssClassName={`${CSS_CLASS_NAME}__action-button -next`}
                        size={ButtonSizes.Small}
                        disabled={isNextButtonDisabled}
                        onClick={() => setShowPageSelector(true)}>
                        {t("choose-a-page-button-text")}
                    </Button>
                )}
            </div>
        </section>
    );
};

export default NotebookBlockSelector;
